"use client"

import { FC, useRef } from "react"
import { Content, asLink } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  type Variants,
  useScroll,
  useTransform,
  useSpring,
} from "motion/react"
import { cn, sliceLabel } from "@/lib/utils"
import { DecorativeLine } from "@/components/ui/decorative-line"

export type HeroProfileIntroProps =
  SliceComponentProps<Content.HeroProfileIntroSlice> & {
    quoteClassName?: string
  }

// 7 floating gold ember particles
const particles = [
  { size: 3, top: "14%", left: "7%", dur: 5.5, delay: 0 },
  { size: 4, top: "22%", left: "87%", dur: 7.0, delay: 1.2 },
  { size: 3, top: "62%", left: "11%", dur: 6.0, delay: 0.5 },
  { size: 5, top: "72%", left: "82%", dur: 8.0, delay: 2.0 },
  { size: 3, top: "42%", left: "93%", dur: 6.5, delay: 0.8 },
  { size: 4, top: "83%", left: "22%", dur: 7.5, delay: 1.5 },
  { size: 3, top: "35%", left: "50%", dur: 9.0, delay: 3.0 },
]

const vHeroStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13 } },
}

const vFadeSlideUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
  },
}

const vIconBounce: Variants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 280, damping: 11 },
  },
}

const HeroProfileIntro: FC<HeroProfileIntroProps> = ({
  slice,
  quoteClassName,
}) => {
  const { primary } = slice
  const ctaLinks = Array.isArray(primary.cta) ? primary.cta : []
  const sectionRef = useRef<HTMLElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  })

  // Background image parallax — drifts downward at 30% of scroll speed
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"])
  const smoothBgY = useSpring(bgY, { stiffness: 60, damping: 20 })

  // Overlay deepens as you scroll (draws focus away)
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.65], [0.72, 0.94])

  // Content floats upward and fades as section scrolls out
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"])
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0])
  const smoothContentY = useSpring(contentY, { stiffness: 50, damping: 20 })

  // Decorative mandala ring spins faster as you scroll down
  const ringRotate = useTransform(scrollYProgress, [0, 1], [0, 180])

  return (
    <LazyMotion features={domAnimation}>
      <section
        ref={sectionRef}
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={sliceLabel(slice.slice_type)}
        className="relative flex min-h-[85dvh] items-center overflow-hidden"
      >
        {/* ── Parallax background image ── */}
        <motion.div
          className="absolute inset-0 scale-110 will-change-transform"
          style={{ y: smoothBgY }}
        >
          {primary.background_image?.url && (
            <PrismicNextImage
              field={primary.background_image}
              fallbackAlt=""
              fill
              className="object-cover object-center"
              priority
              fetchPriority="high"
              imgixParams={{ auto: ["format", "compress"], q: 75 }}
            />
          )}
        </motion.div>

        {/* ── Scroll-driven overlay darkening ── */}
        <motion.div
          aria-hidden
          className="absolute inset-0"
          style={{
            opacity: overlayOpacity,
            background:
              "linear-gradient(125deg, oklch(0.15 0.07 22) 0%, oklch(0.20 0.09 25 / 0.9) 45%, oklch(0.22 0.09 30 / 0.55) 100%)",
          }}
        />

        {/* ── Radial vignette ── */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 80% at 50% 50%, transparent 30%, oklch(0.12 0.06 20 / 0.6) 100%)",
          }}
        />

        {/* ── Floating gold ember particles ── */}
        {particles.map((p, i) => (
          <motion.div
            key={i}
            aria-hidden
            className="pointer-events-none absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              top: p.top,
              left: p.left,
              background: "var(--sacred-gold)",
            }}
            animate={{ y: [0, -18, 0], opacity: [0.2, 0.55, 0.2] }}
            transition={{
              duration: p.dur,
              delay: p.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* ── Scroll-driven mandala ring — center bg decoration ── */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-1/2"
          style={{ rotate: ringRotate }}
        >
          <svg
            width="520"
            height="520"
            viewBox="0 0 520 520"
            fill="none"
            className="opacity-[0.06]"
          >
            <circle
              cx="260"
              cy="260"
              r="250"
              stroke="white"
              strokeWidth="1"
              strokeDasharray="5 12"
            />
            <circle
              cx="260"
              cy="260"
              r="200"
              stroke="white"
              strokeWidth="0.7"
              strokeDasharray="3 16"
            />
            <circle
              cx="260"
              cy="260"
              r="140"
              stroke="white"
              strokeWidth="1"
              strokeDasharray="8 7"
            />
            {Array.from({ length: 8 }).map((_, k) => {
              const angle = (k * 45 * Math.PI) / 180
              return (
                <line
                  key={k}
                  x1="260"
                  y1="260"
                  x2={260 + Math.cos(angle) * 140}
                  y2={260 + Math.sin(angle) * 140}
                  stroke="white"
                  strokeWidth="0.6"
                  opacity="0.8"
                />
              )
            })}
          </svg>
        </motion.div>

        {/* ── Content (fades + drifts up on scroll) ── */}
        <motion.div
          className="relative z-10 container flex flex-col items-center py-11 text-center text-white sm:py-12 md:py-14 lg:py-16 xl:py-20"
          style={{ y: smoothContentY, opacity: contentOpacity }}
        >
          <m.div
            className="flex w-fit flex-col items-center gap-4"
            variants={vHeroStagger}
            initial="hidden"
            animate="visible"
          >
            {/* Icon with double pulse rings */}
            {primary.icon?.url && (
              <m.div
                variants={vIconBounce}
                className="relative flex items-center justify-center"
              >
                {/* Outer pulse ring */}
                <motion.span
                  aria-hidden
                  className="pointer-events-none absolute rounded-full border-2"
                  style={{ borderColor: "var(--sacred-gold)", inset: "-4px" }}
                  animate={{ scale: [1, 1.6, 2.0], opacity: [0.5, 0.2, 0] }}
                  transition={{
                    duration: 2.6,
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                />
                {/* Inner pulse ring */}
                <motion.span
                  aria-hidden
                  className="pointer-events-none absolute rounded-full border"
                  style={{ borderColor: "var(--sacred-gold)", inset: "-4px" }}
                  animate={{ scale: [1, 1.9, 2.5], opacity: [0.35, 0.12, 0] }}
                  transition={{
                    duration: 2.6,
                    delay: 1.1,
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                />
                {/* Gold halo glow */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute rounded-full opacity-30 blur-xl"
                  style={{ background: "var(--sacred-gold)", inset: "0" }}
                />
                <div className="drop-shadow-2xl">
                  <PrismicNextImage
                    field={primary.icon}
                    fallbackAlt=""
                    width={80}
                    height={80}
                    className="size-40 object-contain"
                    imgixParams={{ auto: ["format", "compress"], q: 75 }}
                  />
                </div>
              </m.div>
            )}

            {/* Main title */}
            <m.div
              variants={vFadeSlideUp}
              className="flex flex-col items-center gap-4"
            >
              <PrismicRichText field={primary.main_titlte} />
              <DecorativeLine />
            </m.div>

            {/* Subtitle */}
            {primary.subtitle && (
              <m.p
                variants={vFadeSlideUp}
                className="header6 font-semibold text-amber-200"
              >
                {primary.subtitle}
              </m.p>
            )}

            {/* Quote block */}
            {(primary.quote || primary.quote_translation) && (
              <m.div
                variants={vFadeSlideUp}
                className="flex flex-col gap-4 rounded-xl border p-4 backdrop-blur-sm"
                style={{
                  borderColor: "oklch(0.85 0.12 75 / 0.3)",
                  background: "oklch(0.21 0.085 22 / 0.45)",
                }}
              >
                {primary.quote && (
                  <PrismicRichText
                    field={primary.quote}
                    components={{
                      paragraph: ({ children }) => (
                        <p
                          className={cn(
                            "header4 font-normal italic",
                            quoteClassName,
                          )}
                          style={{ color: "var(--sacred-gold)" }}
                        >
                          {children}
                        </p>
                      ),
                    }}
                  />
                )}
              </m.div>
            )}

            {/* Description */}
            {primary.description && (
              <m.div variants={vFadeSlideUp}>
                <PrismicRichText
                  field={primary.description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-white/80 italic">{children}</p>
                    ),
                  }}
                />
              </m.div>
            )}

            {/* CTA Buttons */}
            {ctaLinks.length > 0 && (
              <m.div
                variants={vFadeSlideUp}
                className="mt-2 flex flex-wrap justify-center gap-3"
              >
                {ctaLinks.map((link, i) => {
                  const href = asLink(link)
                  const text =
                    "text" in link && link.text
                      ? String(link.text)
                      : "Learn More"
                  const isPrimary =
                    "variant" in link && link.variant === "Primary"
                  return (
                    <motion.a
                      key={i}
                      href={href ?? "#"}
                      whileHover={{ scale: 1.04, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 18,
                      }}
                      className={cn(
                        "inline-flex items-center rounded-lg px-6 py-3 text-base font-semibold transition-colors",
                        isPrimary
                          ? "bg-orange-700 text-white hover:bg-orange-800"
                          : "border border-white/40 bg-white/10 text-white hover:bg-white/20",
                      )}
                    >
                      {text}
                    </motion.a>
                  )
                })}
              </m.div>
            )}
          </m.div>
        </motion.div>

        {/* ── Bottom edge fade into next section ── */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
          style={{
            background:
              "linear-gradient(to top, oklch(0.12 0.06 20 / 0.7), transparent)",
          }}
        />
      </section>
    </LazyMotion>
  )
}

export default HeroProfileIntro
